import React from 'react';
import Icone from './Icone';
import { molduraCampo } from './Input';
import { useCampo } from './Field';

/**
 * Seleção do design system (at-58, frente C). Mesma moldura do `Input`, com a
 * seta desenhada por nós.
 *
 * ⚠️ **`appearance: none` + seta própria**: com o `preflight` desligado, o
 * `<select>` nativo traz a seta do sistema, que muda de forma entre Android, iOS
 * e desktop e não acompanha a borda arredondada. A seta vem do `Icone`, então
 * herda a cor do estado.
 *
 * 📌 Contra o CSS legado este componente **não precisa de marcador**: o global de
 * `associado.css` é `select { font-size: 16px }` — um seletor de ELEMENTO,
 * especificidade (0,0,1) —, que perde para qualquer utilitário `tw-` (0,1,0).
 * Diferente do `input[type="text"]`, que tem o atributo e ganha. Ver `Input.tsx`.
 * (Aquela regra continua valendo para as telas legadas: a at-58 a resgatou para
 * `ui/tokens/legado-compartilhado.css`, porque removeu o único import dela.)
 */

export interface OpcaoSelect {
  valor: string;
  rotulo: string;
}

interface PropsSelect extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  opcoes: readonly OpcaoSelect[] | readonly string[];
  /**
   * Primeira opção, de valor vazio. Sem ela o campo já nasce "respondido".
   *
   * ⚠️ **String vazia REMOVE a opção** (at-65). É o caso do campo que já tem uma
   * resposta neutra na própria lista — "Todos", nos filtros da carteira: ali a
   * opção em branco seria **selecionável**, gravaria `''` em vez de `'TODOS'` e
   * faria o contador de filtros ativos mentir. Um placeholder invisível não é
   * placeholder.
   */
  placeholder?: string;
  erro?: boolean;
}

const Select: React.FC<PropsSelect> = ({
  opcoes,
  placeholder = 'Selecione...',
  erro,
  className = '',
  ...resto
}) => {
  const campo = useCampo();
  const temErro = erro ?? campo?.temErro ?? false;

  const normalizadas: OpcaoSelect[] = opcoes.map((o) =>
    typeof o === 'string' ? { valor: o, rotulo: o } : o,
  );

  return (
    <div className="tw-relative">
      <select
        id={campo?.id}
        aria-invalid={temErro || undefined}
        aria-describedby={campo?.descricao}
        className={[molduraCampo(temErro), 'tw-cursor-pointer tw-pr-10', className]
          .filter(Boolean)
          .join(' ')}
        {...resto}
      >
        {placeholder !== '' && <option value="">{placeholder}</option>}
        {normalizadas.map(({ valor, rotulo }) => (
          <option key={valor} value={valor}>
            {rotulo}
          </option>
        ))}
      </select>

      <span
        className="tw-pointer-events-none tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-texto-desabilitado"
        aria-hidden="true"
      >
        <Icone nome="chevron-baixo" tamanho={16} />
      </span>
    </div>
  );
};

export default Select;
